/*
 * File: /src/store/modules/auth.js
 * Created Date: Sunday March 17th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Tuesday, 2nd April 2019 11:13:54 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */

import { authedAxios, publicAxios } from 'src/shared/axiosWrapper';
import { DEFAULT_PROFILE_PHOTO } from 'src/store/modules/userPhotos';

// This sets the base URL and header via the mixin setup
const jwt = localStorage.getItem('s2-access-token') || undefined;

// initial state
const state = {
  // I tire of changing it in so many places.
  /* move this to app or somewhere else as it is not used that I can find
  companyName: 'SendSquared',
  companyUrl: 'sendsquared.com',
  */

  authAccount: undefined,
  userData: {},
  acls: [],
  aclSet: new Set(),
  brands: [], // Set of brands the user is Auth/Assigned to; from BE
  jwt: jwt,
  agencyPrimaryAccount: {
    id: 0,
    name: 0
  },
  // TODO: This should be moved elsewhere as its not auth related
  brandIds: undefined, // Global brand filters, unset means all
  // Once its set it will filter to those ids, might be empty for none
  longTokenMode: false, // Long lasting tokens for realtime data. Very limited scope on what they can see to prevent data leaks
};

// getters
const getters = {


  getCompanyName: state => {
    if (!state.authAccount?.companies) {
      return;
    }
    const company = state.authAccount.companies.find(c => c.id === state.authAccount.companyId);
    return company?.name;
  },
  getCompanyUuid: state => {
    if (!state.authAccount?.companies) {
      return;
    }
    const company = state.authAccount.companies.find(c => c.id === state.authAccount.companyId);
    return company?.uuid;
  },

  getEmulateMode: state => state.authAccount ? state.authAccount.sourceId : undefined,

  getAgencyMode: state => {
    return state.agencyPrimaryAccount && state.authAccount ? state.agencyPrimaryAccount === state.authAccount.companyId : false;
  },
  getAgencyPrimary: state => state.agencyPrimaryAccount,

  getLoginStatus: state => (state.jwt ? true : false),
  getJwtString: state => state.jwt,
  getLoginAccount: state => state.authAccount,
  getLoginAccountName: state => state.authAccount ? state.authAccount.name : undefined,
  getAccountUsername: state => state.authAccount ? state.authAccount.username : undefined,
  getAccountCompany: state => state.authAccount ? state.authAccount.companyId : undefined,
  getUserData: state => state.userData,
  getProfilePhoto: state => state.authAccount?.photoUrl || DEFAULT_PROFILE_PHOTO,
  getCompanyById: state => id => {
    for (const inc in state?.authAccount?.companies) {
      if (id === state?.authAccount?.companies[inc].id) {
        return state.authAccount.companies[inc];
      }
    }
  },
  // TODO Kill this one or at least reanme it
  getAuthCompanies: state => state?.authAccount?.companies,

  /**
   * Get ACLs
   * @param {*} state
   * @returns
   */
  getAcls: state => aclCheck => {
    return state.aclSet.has(aclCheck);
  },
  getAuthBrands: state => state?.brands,
  getGlobalBrandIds: state => {
    return state.brandIds;
  },
  getLongTokenMode: state => {
    return state.longTokenMode;
  },
};

// actions
const actions = {
  setLoggedIn({ commit }, authVals) {
    // commit('setAuthStatus', true);
    commit('setAuthAccount', authVals.authAccount);
    commit('setAuthAcls', authVals.authAcls);
    commit('setAuthBrands', authVals.authBrands);
  },

  /**
   * Login the User
   * @param authData username and password combination
   */
  processLogin({ commit, dispatch }, authData) {
    const data = authData;
    localStorage.setItem('s2-access-token', data.jwt);
    localStorage.setItem('s2-refresh-token', data.refreshToken);
    if (data.trustedId) {
      localStorage.setItem('s2-trusted-device', data.trustedId);
    }
    commit('setJwt', data.jwt, false);

    // This should only be updated when getting a new token
    if (data.companies.length > 0) {
      // TODO: DO we really need this?
      commit('setAgencyParentAccount', {
        id: data.companyId,
        name: data.companyName
      });
    }

    commit('setAuthAccount', {
      username: authData.username,
      name: data.name,
      userId: data.userId,
      companyId: data.companyId,
      photoAssetId: data.photoAssetId || null,
      photoUrl: DEFAULT_PROFILE_PHOTO,
      companies: data.companies,
    });

    if (data.photoAssetId) {
      dispatch('userPhotos/resolvePhotoUrl', data.photoAssetId, { root: true }).then(url => {
        commit('setProfilePhoto', { photoAssetId: data.photoAssetId, photoUrl: url });
      });
    }
    // This needs to be a defined model, overwrite prone
    commit('setUserData', {
      lastAirbnbSeen: data.lastAirbnbSeen,
      lastSmsSeen: data.lastSmsSeen,
      lastMessageSeen: data.lastMessageSeen,
      lastVoicemailSeen: data.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
    // Add Acls - This will need to be checked at routes
    //  - also lot's of back and forth, as this is easily falsified.
    commit('setAuthAcls', data.acls);
    commit('setAuthBrands', data.brands);

    // Set Global Brands Ids from defaults
    commit('setGlobalBrandIds', data.defaultBrands);
  },

  /* Replace the current stored refreshToken with a long duration token
   * This will log the user out essentially and will require a new token from an
   * authorized user after the refresh token expires
   */
  setLongToken: ( { commit }, data) => {
      localStorage.setItem('s2-access-token', data.token);
      localStorage.setItem('s2-refresh-token', data.refreshToken);
      commit('setJwt', data.token, true);
      commit('setAuthAccount', {
        username: '',
        name: '',
        userId: 0,
        companyId: state.authAccount.companyId,
      });
      commit('setAuthAcls', []);
      commit('setAgencyParentAccount', {});
      commit('setLongTokenMode', true);
  },
  // One off for when reloading jwt on refresh
  setLongTokenMode: ({ commit }, mode) => {
      commit('setLongTokenMode', mode);
  },

  /**
   * Emulate Users Session
   * @param data emulated token object
   */
  emulate: ({ commit, dispatch }, data) => {
    localStorage.setItem('s2-access-token', data.jwt);
    localStorage.setItem('s2-refresh-token', data.refreshToken);
    commit('setJwt', data.jwt, false);
    // This should all come from the JWT. Why do we sent it twice?
    commit('setAuthAccount', {
      username: data.email,
      name: data.name,
      userId: data.userId,
      companyId: data.companyId,
      photoAssetId: data.photoAssetId || null,
      photoUrl: DEFAULT_PROFILE_PHOTO,
      sourceId: data.sourceId, // Will be undefined on stop emulation
      companies: data.companies,
    });

    if (data.photoAssetId) {
      dispatch('userPhotos/resolvePhotoUrl', data.photoAssetId, { root: true }).then(url => {
        commit('setProfilePhoto', { photoAssetId: data.photoAssetId, photoUrl: url });
      });
    }
    // TODO: Validate that Acl data is here
    commit('setAuthAcls', data.acls);
    commit('setAuthBrands', data.brands);
    commit('setUserData', {
      lastSmsSeen: data.lastSmsSeen,
      lastMessageSeen: data.lastMessageSeen,
      lastVoicemailSeen: data.lastVoicemailSeen,
      lastWhatsappSeen: data.lastWhatsappSeen,
    });
  },

  setAgency: ({ commit, state }, data) => {
    localStorage.setItem('s2-access-token', data.jwt);
    commit('setJwt', data.jwt, false);
    // Just update the parts that change for an agency,
    const authAccount = Object.assign({}, state.authAccount);

    // Just replace the specific attrs that change for agency
    authAccount.companyId = data.companyId;
    authAccount.userId = data.userId;
    authAccount.name = data.name;
    commit('setAuthAccount', authAccount);
    commit('setAuthAcls', data.acls);
    commit('setAuthBrands', data.brands);
  },

  logout({ commit }) {
    localStorage.removeItem('s2-access-token');
    localStorage.removeItem('s2-refresh-token');
    commit('setJwt', undefined, false);
    commit('setAuthAccount', undefined);
    commit('setAuthAcls', []);
    commit('setAgencyParentAccount', {});
    // Keep trusted device only after clear
    const trustedId = localStorage.getItem('s2-trusted-device');
    localStorage.clear();
    if (trustedId) {
      localStorage.setItem('s2-trusted-device', trustedId);
    }
  },

  updatePhoto({ commit }, { photoAssetId, photoUrl }) {
    commit('setProfilePhoto', { photoAssetId, photoUrl });
  },

  // These need to be moved to a user store, odd for now as they are nested and not defined specifically
  // So we only want to update one without crush the entire object
  updateLastAirbnbSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: lastId,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastSmsSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: lastId,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastWhatsappSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen: lastId,
    });
  },
  updateLastMessageSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: lastId,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastVoicemailSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen: lastId,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  setGlobalBrandIds({ commit }, brandIds) {
    commit('setGlobalBrandIds', brandIds);
  },
};

// mutations
const mutations = {
  setLongTokenMode(state, mode) {
    state.longTokenMode = mode ? mode : false;
  },
  setAuthAccount(state, authData) {
    localStorage.setItem('s2-auth-account', authData ? JSON.stringify(authData) : authData);

    state.authAccount = Object.assign({}, authData);
  },
  setAuthAcls(state, acls) {
    localStorage.setItem('s2-auth-acls', acls ? JSON.stringify(acls) : []);
    state.acls = Object.assign([], acls);
    state.aclSet = new Set((acls || []).map(acl => acl.permission));
  },
  setAuthBrands(state, brands) {
    localStorage.setItem('s2-auth-brands', brands ? JSON.stringify(brands) : []);
    state.brands = Object.assign([], brands);
  },
  setProfilePhoto(state, { photoAssetId, photoUrl }) {
    state.authAccount = { ...state.authAccount, photoAssetId, photoUrl };
    localStorage.setItem('s2-auth-account', JSON.stringify(state.authAccount));
  },
  setJwt(state, jwt, mode) {
    state.jwt = jwt;
    state.longTokenMode = mode;
  },
  setUserData(state, meta) {
    state.userData = Object.assign({}, meta);
  },
  // this is in the admin... might want to move this out of here.
  /*
  setCompanyAccounts(state, meta) {
    state.companyAccounts = Object.assign([], meta);
  },
  */
  setAgencyParentAccount(state, meta) {
    state.agencyPrimaryAccount = Object.assign({}, meta);
  },
  setGlobalBrandIds(state, ids) {
    // Expect an array for a filter, might be empty.
    // Or null for no brands filtered, all available to user
    state.brandIds = ids;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
